import {
  ButtonGroup,
  ConnectionGroup,
  Flex,
  PageGridColumn,
  PageGridRow,
  StatusBanner,
  type SingleConnectionGroupItem,
} from "@wayflyer/flyui";
import { IconShop24Line } from "@wayflyer/flyui-icons/24/line";
import type { FundingApplicationSchemas } from "api-types";
import { useState } from "react";
import { useIntl } from "react-intl";
import { ACCOUNTING, BANKING, SHOPPING, MARKETING, useSpaNavigate, filteredAndSortedDataSourcesNext } from "shared-ui";
import { useCustomerAppConfig } from "undercarriage";

import { PAGE_TYPE } from "../../hooks/navigation-next";
import { composePath } from "../../hooks/navigation-next/use-funding-application-navigation-next";
import { usePlatformConnectors } from "../../queries/connectors/queries";
import { useFundingApplication, useFundingApplicationMutation } from "../../queries/funding-application/queries";
import type { SelectedAdditionalPlatform } from "../../utils/checklist-items";
import { getRadioChecklistItemPropsForConnector } from "../../utils/checklist-items";
import { filterConnectorsByStageConfig, getFilteredAccountingConnectors } from "../../utils/connectors";
import { isGoogleAnalyticsRequired } from "../../utils/funding-journey";

import { MESSAGES } from "./message-definitions";

interface AddAnotherPlatformNextProps {
  fundingApplicationStages: FundingApplicationSchemas.CustomerFundingApplicationStagesScreen;
}

export const AddAnotherPlatformNext = ({ fundingApplicationStages }: AddAnotherPlatformNextProps) => {
  const intl = useIntl();

  const {
    data: { customerDomain },
  } = useCustomerAppConfig();

  const { data: fundingApplication } = useFundingApplication(customerDomain);
  const fundingApplicationMutation = useFundingApplicationMutation(customerDomain);
  const { navigate } = useSpaNavigate("FundingApp");

  // Require connections to prevent multiple connections for platforms which do not support multistore
  const { data: connectors } = usePlatformConnectors(customerDomain, { skipConnections: false });
  const filteredandSortedDataSources = filteredAndSortedDataSourcesNext(connectors);
  const [error, setError] = useState<string | null>(null);

  const showRevenue = fundingApplicationStages.revenue?.allowPlatformSelection;
  const showMarketing = fundingApplicationStages.marketing?.allowPlatformSelection;
  const showAccounting = fundingApplicationStages.accounting?.allowPlatformSelection;
  const showBanking = !!fundingApplicationStages.banking;

  const [selectedAdditionalPlatform, setSelectedAdditionalPlatform] = useState<SelectedAdditionalPlatform | null>(null);

  const getPathForConnector = (connectorSlug: string) => {
    return connectorSlug === "ga" && !isGoogleAnalyticsRequired(fundingApplication.selectedConnectorSlugs)
      ? composePath(PAGE_TYPE.SUMMARY)
      : composePath(PAGE_TYPE.CONNECTORS, connectorSlug);
  };

  const onNext = () => {
    if (selectedAdditionalPlatform?.isSupported) {
      if (selectedAdditionalPlatform.category === BANKING) {
        return navigate(composePath(PAGE_TYPE.BANKING));
      }
      // Only adds selected connector if connection is successful
      return navigate(getPathForConnector(selectedAdditionalPlatform.platform));
    }
    // Unsupported platforms
    const selectedUnsupportedEcommerce = [...fundingApplication.selectedUnsupportedEcommerce];
    const selectedUnsupportedMarketingAnalytics = [...fundingApplication.selectedUnsupportedMarketingAnalytics];
    switch (selectedAdditionalPlatform?.category) {
      case SHOPPING:
        selectedUnsupportedEcommerce.push(selectedAdditionalPlatform.platform);
        break;
      case MARKETING:
        selectedUnsupportedMarketingAnalytics.push(selectedAdditionalPlatform.platform);
        break;
    }
    return fundingApplicationMutation
      .mutateAsync({
        id: fundingApplication.id,
        selectedUnsupportedEcommerce,
        selectedUnsupportedMarketingAnalytics,
      })
      .then(() => navigate(composePath(PAGE_TYPE.SUMMARY)));
  };

  const onSubmit = () => {
    if (selectedAdditionalPlatform) {
      onNext();
    } else {
      setError(intl.formatMessage(MESSAGES.selectAnAdditionalPlatformError));
      // Scroll to top to show the error
      window.scroll({ top: 0, left: 0, behavior: "smooth" });
    }
  };

  const isPlatformSelected = (platform: string, isSupported: boolean) => {
    return !!(
      selectedAdditionalPlatform &&
      selectedAdditionalPlatform.platform === platform &&
      // prevents overlap of supported and unsupported platforms e.g. if freetext matches a supported platform
      selectedAdditionalPlatform.isSupported === isSupported
    );
  };

  const constructSupportedPlatformChecklistItemProps = (
    connectors: FundingApplicationSchemas.ConnectorPropertiesSchema[]
  ) => {
    return connectors
      .filter(
        (connector) =>
          connector.multipleConnectionsAllowed ||
          (!connector.multipleConnectionsAllowed && connector.connections.length === 0)
      )
      .map((connector) =>
        getRadioChecklistItemPropsForConnector(
          connector,
          isPlatformSelected(connector.slug, true),
          setSelectedAdditionalPlatform
        )
      );
  };

  const supportedPlatformOptions: Record<string, SingleConnectionGroupItem[]> = {
    [SHOPPING]: constructSupportedPlatformChecklistItemProps(
      filterConnectorsByStageConfig(
        filteredandSortedDataSources.shoppingDataSources,
        fundingApplicationStages.revenue
        // Temporary: Filter out Magento until fully removed from codebase
      ).filter((connector) => connector.slug !== "magento2")
    ),
    [MARKETING]: constructSupportedPlatformChecklistItemProps(
      filterConnectorsByStageConfig(
        filteredandSortedDataSources.marketingDataSources,
        fundingApplicationStages.marketing
      )
    ),
    [ACCOUNTING]: constructSupportedPlatformChecklistItemProps(
      filterConnectorsByStageConfig(
        getFilteredAccountingConnectors(filteredandSortedDataSources),
        fundingApplicationStages.accounting
      )
    ),
    [BANKING]: [
      {
        checked: isPlatformSelected(BANKING, true),
        label: intl.formatMessage(MESSAGES.addNewBankPlatform),
        value: BANKING,
        logo: <IconShop24Line />,
        name: "banking-platform",
        onChange: () => setSelectedAdditionalPlatform({ platform: BANKING, category: BANKING, isSupported: true }),
      },
    ],
  };

  return (
    <PageGridColumn $gap="6">
      {error ? (
        <PageGridRow>
          <StatusBanner variant="critical" subheading={error} />
        </PageGridRow>
      ) : null}
      {showRevenue && (
        <ConnectionGroup
          selectionMode="single"
          categoryLabel={intl.formatMessage(MESSAGES.onlineSalesPlatformCategory)}
          options={supportedPlatformOptions[SHOPPING]}
        />
      )}
      {showMarketing && supportedPlatformOptions[MARKETING].length > 0 && (
        <ConnectionGroup
          selectionMode="single"
          categoryLabel={intl.formatMessage(MESSAGES.marketingPlatformCategory)}
          options={supportedPlatformOptions[MARKETING]}
        />
      )}
      {showAccounting && supportedPlatformOptions[ACCOUNTING].length > 0 && (
        <ConnectionGroup
          selectionMode="single"
          categoryLabel={intl.formatMessage(MESSAGES.accountingPlatformCategory)}
          options={supportedPlatformOptions[ACCOUNTING]}
        />
      )}
      {showBanking && (
        <ConnectionGroup
          selectionMode="single"
          categoryLabel={intl.formatMessage(MESSAGES.bankingPlatformCategory)}
          options={supportedPlatformOptions[BANKING]}
        />
      )}

      <Flex>
        <ButtonGroup
          primaryAction={{
            onAction: onSubmit,
            content: intl.formatMessage(MESSAGES.addAnotherPlatformContinueButton),
            analyticsId: "add-another-platforms-continue",
          }}
          tertiaryAction={{
            onAction: () => navigate(composePath(PAGE_TYPE.SUMMARY)),
            content: intl.formatMessage(MESSAGES.addAnotherPlatformCancel),
            analyticsId: "add-another-platform-cancel",
          }}
        />
      </Flex>
    </PageGridColumn>
  );
};
